import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema,
} from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';
import { AppConfigService } from '@config/app-config.service';
import { DeviceService } from '@device/device.service';
import { environment } from '@environments/environment';
import { EventService } from '@event/event.service';
import { Participant } from '@model/participant';
import { PushNotification } from '@model/push-notification';
import {
  createDebugPushTopic,
  createLivePushTopic,
  createNotificationsPushTopic,
  createParticipantPushTopic,
  createPushNotificationFromSchema,
} from '@util/push-notification-util';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private deviceService = inject(DeviceService);
  private router = inject(Router);
  private eventService = inject(EventService);
  private appConfigService = inject(AppConfigService);

  private isPushAvailable = false;

  private _lapNotification = signal<PushNotification | undefined>(undefined);
  private _pushNotification = signal<PushNotification | undefined>(undefined);
  private _liveNotification = signal<PushNotification | undefined>(undefined);

  lapNotification = computed<PushNotification | undefined>(() =>
    this._lapNotification(),
  );
  liveNotification = computed<PushNotification | undefined>(() =>
    this._liveNotification(),
  );
  pushNotification = computed<PushNotification | undefined>(() =>
    this._pushNotification(),
  );

  private async addListeners(followees: Participant[] = []): Promise<void> {
    await PushNotifications.addListener('registration', token => {
      console.log(
        'PushNotificationService.Listener.registration.token',
        token.value,
      );

      void this.managePushSubscriptions(followees);
    });

    await PushNotifications.addListener(
      'registrationError',
      (error: unknown) => {
        console.log(
          'PushNotificationService.Listener.registrationError',
          error,
        );
      },
    );

    // Method called when a notification is received
    await PushNotifications.addListener(
      'pushNotificationReceived',
      pushNotificationSchema => {
        console.log(
          'PushNotificationService.Listener.pushNotificationReceived.pushNotification',
          pushNotificationSchema,
        );

        this.processPushNotificationSchema(pushNotificationSchema);
      },
    );

    // Method called when tapping on a notification
    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      actionPerformed => {
        console.log(
          'PushNotificationService.Listener.pushNotificationActionPerformed.actionPerformed',
          actionPerformed,
        );

        this.processPushNotificationAction(actionPerformed);
      },
    );
  }

  private processPushNotificationSchema(
    pushNotificationSchema: PushNotificationSchema,
  ): PushNotification | undefined {
    // No UI actions in this method. This is the logic for processing the data from a notification
    // When navigation to a part of the app is needed, this needs to be done when an action is performed
    const pushNotification = createPushNotificationFromSchema(
      pushNotificationSchema,
    );

    if (!pushNotification) {
      return;
    }

    switch (pushNotification.type) {
      case 'notification':
        this._pushNotification.set(pushNotification);
        break;
      case 'lap':
        this._lapNotification.set(pushNotification);
        break;
      case 'live':
        this._liveNotification.set(pushNotification);
        break;
      default:
        break;
    }

    return pushNotification;
  }

  private processPushNotificationAction(
    actionPerformed: ActionPerformed,
  ): void {
    const pushNotification = createPushNotificationFromSchema(
      actionPerformed.notification,
    );

    if (!pushNotification) {
      return;
    }

    if (pushNotification.type === 'lap') {
      const event = this.eventService.event();

      if (event?.isEventLive) {
        const { participantUuid } = pushNotification;

        void this.router.navigate(['ad6', 'live'], {
          queryParams: { participantUuid },
          replaceUrl: true,
        });
      }
    } else if (pushNotification.type === 'notification') {
      void this.router.navigate(['ad6', 'start'], {
        replaceUrl: true,
      });
    }
  }

  private async subscribeTo(topic?: string): Promise<void> {
    if (topic && this.isPushAvailable) {
      await FCM.subscribeTo({ topic }).catch(error => {
        console.error(`FCM.subscribeTo('${topic}') failed!`, error);
      });
    }
  }

  private async unsubscribeFrom(topic?: string): Promise<void> {
    if (topic && this.isPushAvailable) {
      await FCM.unsubscribeFrom({ topic }).catch(error => {
        console.error(`FCM.unsubscribeFrom('${topic}') failed!`, error);
      });
    }
  }

  //
  // TODO: TEST the PushNotifications.checkPermissions(), PushNotifications.requestPermissions(), PushNotifications.register() and addListeners()
  async init(followees: Participant[] = []): Promise<void> {
    const platform = Capacitor.getPlatform();

    const deviceInfo = this.deviceService.deviceInfo();
    const appConfig = this.appConfigService.appConfig();

    this.isPushAvailable = platform === 'android' || platform === 'ios';

    if (this.isPushAvailable) {
      let permissionStatus = await PushNotifications.checkPermissions();

      if (
        permissionStatus.receive === 'prompt' ||
        permissionStatus.receive === 'prompt-with-rationale'
      ) {
        permissionStatus = await PushNotifications.requestPermissions();
      }

      if (permissionStatus.receive === 'granted') {
        await PushNotifications.removeAllListeners();
        await this.addListeners(followees);
        if (environment.name === 'TEST' && deviceInfo) {
          await this.subscribeTo(createDebugPushTopic(deviceInfo, appConfig));
        }
        await PushNotifications.register();
      } else if (permissionStatus.receive === 'denied') {
        await PushNotifications.unregister();
        await PushNotifications.removeAllListeners();
      }
    }
  }

  private async managePushSubscriptions(
    followees: Participant[],
  ): Promise<void> {
    const appConfig = this.appConfigService.appConfig();

    followees.map(followee => void this.subscribeToParticipant(followee));

    const pushTopicNotificationsTopic = createNotificationsPushTopic(appConfig);

    await this.subscribeTo(pushTopicNotificationsTopic);
  }

  private pushTopicParticipant(participant: Participant): string | undefined {
    const event = this.eventService.event();

    return createParticipantPushTopic(participant, event);
  }

  async subscribeToParticipant(participant: Participant): Promise<void> {
    await this.subscribeTo(this.pushTopicParticipant(participant));
  }

  async unsubscribeFromParticipant(participant: Participant): Promise<void> {
    await this.unsubscribeFrom(this.pushTopicParticipant(participant));
  }

  async subscribeToLive(): Promise<void> {
    await this.subscribeTo(createLivePushTopic(this.appConfigService.appConfig()));
  }

  async unsubscribeFromLive(): Promise<void> {
    await this.unsubscribeFrom(createLivePushTopic(this.appConfigService.appConfig()));
  }
}
